// TestimonialsManager — CRUD for guest testimonials.
import React, { useEffect, useState } from 'react';
import { Edit, Loader2, Plus, Star, Trash2 } from 'lucide-react';
import { apiCall } from './_shared';
import { ReviewImportPanel } from './ReviewImportPanel';
import { ReviewReplyAssistant } from './ReviewReplyAssistant';
import GuestPhotosModeration from './GuestPhotosModeration';
import { ReviewHighlightsCard, ReviewLangChips, ReviewTranslationsBar } from './ReviewTranslationsBar';
import { PulseCard } from './GrowthCards';

const TestimonialsManager = ({ userRole }) => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [newTestimonial, setNewTestimonial] = useState({ name: '', date: '', rating: 9, text: '', source: 'Booking.com', country: '' });

  const canEdit = ['super_admin', 'admin'].includes(userRole);

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const data = await apiCall('/api/admin/testimonials');
      setTestimonials(data.testimonials || []);
    } catch (err) {
      console.error('Error fetching testimonials:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      await apiCall('/api/admin/testimonials', {
        method: 'POST',
        body: JSON.stringify(newTestimonial),
      });
      await fetchTestimonials();
      setShowAddModal(false);
      setNewTestimonial({ name: '', date: '', rating: 9, text: '', source: 'Booking.com', country: '' });
    } catch (err) {
      alert('Error adding: ' + err.message);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await apiCall(`/api/admin/testimonials/${id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm),
      });
      await fetchTestimonials();
      setEditingId(null);
    } catch (err) {
      alert('Error updating: ' + err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this testimonial?')) return;
    try {
      await apiCall(`/api/admin/testimonials/${id}`, { method: 'DELETE' });
      await fetchTestimonials();
    } catch (err) {
      alert('Error deleting: ' + err.message);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-orange-500" /></div>;
  }

  // Rows precomputed OUTSIDE JSX (Babel workaround).
  const cards = testimonials.map((t) => (
    <div key={t.id} className="bg-white rounded-xl p-4 shadow-sm border">
      {editingId === t.id ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <input
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="px-3 py-2 border rounded-lg"
              placeholder="Name"
            />
            <input
              type="date"
              value={editForm.date}
              onChange={(e) => setEditForm({ ...editForm, date: e.target.value })}
              className="px-3 py-2 border rounded-lg"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">Rating (1–10):</span>
            <input type="number" min={1} max={10} value={editForm.rating} onChange={(e) => setEditForm({ ...editForm, rating: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} className="w-20 px-2 py-1 border rounded" data-testid="testimonial-edit-rating" />
            <input value={editForm.source || ''} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })} className="px-3 py-1 border rounded" placeholder="Source (Booking.com, Google…)" data-testid="testimonial-edit-source" />
            <input value={editForm.country || ''} onChange={(e) => setEditForm({ ...editForm, country: e.target.value })} className="px-3 py-1 border rounded" placeholder="Guest country" data-testid="testimonial-edit-country" />
          </div>
          <textarea
            value={editForm.text}
            onChange={(e) => setEditForm({ ...editForm, text: e.target.value })}
            className="w-full px-3 py-2 border rounded-lg h-24"
            placeholder="Review text"
          />
          <div className="flex gap-2">
            <button onClick={() => handleUpdate(t.id)} className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600">Save</button>
            <button onClick={() => setEditingId(null)} className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-gray-900">{t.name}</h3>
              <div className="flex">
                {[1, 2, 3, 4, 5].map(n => (
                  <Star key={n} className={`w-4 h-4 ${n <= Math.round(t.rating / 2) ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-xs font-semibold text-orange-600">{t.rating}/10</span>
            </div>
            <p className="text-sm text-gray-500">{t.date}{t.source ? ` · ${t.source}` : ''}{t.country ? ` · ${t.country}` : ''}</p>
            <p className="text-gray-600 mt-2">{t.text}</p>
            <ReviewLangChips review={t} />
          </div>
          {canEdit && (
            <div className="flex gap-2">
              <button onClick={() => { setEditingId(t.id); setEditForm(t); }} className="p-2 hover:bg-gray-100 rounded">
                <Edit className="w-4 h-4 text-gray-500" />
              </button>
              <button onClick={() => handleDelete(t.id)} className="p-2 hover:bg-gray-100 rounded">
                <Trash2 className="w-4 h-4 text-red-500" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  ));

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Testimonials ({testimonials.length})</h2>
        {canEdit && (
          <button onClick={() => setShowAddModal(true)} className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">
            <Plus className="w-4 h-4" /> Add Testimonial
          </button>
        )}
      </div>

      {['super_admin', 'admin', 'manager', 'marketing'].includes(userRole) && <GuestPhotosModeration />}
      {['super_admin', 'admin', 'manager', 'marketing'].includes(userRole) && <ReviewReplyAssistant testimonials={testimonials} />}
      {canEdit && <ReviewImportPanel onImported={fetchTestimonials} />}
      <ReviewTranslationsBar onChanged={fetchTestimonials} />
      <ReviewHighlightsCard />
      <PulseCard />
      <div className="grid gap-4">{cards}</div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full m-4">
            <h3 className="text-lg font-semibold mb-4">Add Testimonial</h3>
            <div className="space-y-4">
              <input
                value={newTestimonial.name}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
                placeholder="Guest name"
              />
              <input
                type="date"
                value={newTestimonial.date}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg"
              />
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-gray-600">Rating (1–10):</span>
                <input type="number" min={1} max={10} value={newTestimonial.rating} onChange={(e) => setNewTestimonial({ ...newTestimonial, rating: Math.max(1, Math.min(10, Number(e.target.value) || 1)) })} className="w-20 px-2 py-1 border rounded" data-testid="testimonial-new-rating" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <input value={newTestimonial.source} onChange={(e) => setNewTestimonial({ ...newTestimonial, source: e.target.value })} className="px-3 py-2 border rounded-lg" placeholder="Source (Booking.com, Google…)" data-testid="testimonial-new-source" />
                <input value={newTestimonial.country} onChange={(e) => setNewTestimonial({ ...newTestimonial, country: e.target.value })} className="px-3 py-2 border rounded-lg" placeholder="Guest country" data-testid="testimonial-new-country" />
              </div>
              <textarea
                value={newTestimonial.text}
                onChange={(e) => setNewTestimonial({ ...newTestimonial, text: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg h-24"
                placeholder="Review text"
              />
            </div>
            <div className="flex gap-2 mt-6">
              <button onClick={handleAdd} className="flex-1 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600">Add</button>
              <button onClick={() => setShowAddModal(false)} className="flex-1 py-2 bg-gray-200 rounded-lg hover:bg-gray-300">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestimonialsManager;
